// src/features/policies/page/Category

import DynamicTable from '@/common/components/DynamicTable/DynamicTable';
import type { ColumnsType } from 'antd/es/table';
import { API_ENDPOINTS, QUERY_KEYS } from '../../api/apiEndpoints';
import type { SearchPolicyType } from '../../types/policiesTypes';
import dayjs from 'dayjs';
import CancelModal from '@/common/components/CancelModal';
import Confirmation from '@/common/components/ConfirmButton';
import { CheckCircleFilled } from '@ant-design/icons';
import { useState } from 'react';
import type { Key } from 'antd/es/table/interface';
import StatusTag from '@/common/components/Status';
import { Button } from 'antd';
import { router } from '../../../../app/routing';
import { POLICY_HOLDER_STATUS_OPTIONS } from '../../../../common/staticData/options';
import { useTranslation } from 'react-i18next';

const SearchPolicy = () => {
    const [selectedPolicyRowKeys, setSelectedPolicyRowKeys] = useState<Key[]>([]);
    const { t } = useTranslation();

    const handleRowSelectionChange = (selectedRowKeys: Key[]) => {
        setSelectedPolicyRowKeys(selectedRowKeys);
        ///console.log("Selected Policy IDs:", selectedRowKeys);
    };
   
    const myActions = [
        {
            render: (record: SearchPolicyType) => {
                if (record.status === "Created") {
                    return (
                        <Confirmation
                            apiUrl={API_ENDPOINTS.POLICIES.ACTIVATE_POLICY}
                            id={record.id}
                            title={t("Activate")}
                            icon={<CheckCircleFilled />}
                            method="post"
                            payloadKey="Id"
                            confirmButtonText={t("Are you sure you want to 'Activate' this policy?")}
                            queryKey={QUERY_KEYS.POLICIES.Policy}
                            queryKeySecond={"PolicyHolderSearch"}
                            successMessage={t("Policy activated successfully")}
                />
              );
            }
            if (record.status === "Active") {
              return (
                  <CancelModal
                      apiUrl={API_ENDPOINTS.POLICIES.CANCEL_POLICY}
                      record={record}
                      title={t("Cancel")}
                      confirmButtonText={t("Are you sure you want to 'Cancel' this policy?")}
                      queryKey={QUERY_KEYS.POLICIES.Policy}
                      queryKeySecond={"PolicyHolderSearch"}
                      successMessage={t("Policy cancelled successfully")}
                />
              );
            }
            return <span></span>;
          },
        },
      ];

    // Define columns for the SearchPolicy table
    const SearchPolicyColumns: ColumnsType<SearchPolicyType> = [
        {
            title: 'Code',
            key: 'code',
            render: (_, record) => (
                <Button
                    type="link"
                    onClick={() => {
                        router.navigate({
                            to: '/policies/search-policy/detail', // Corrected path
                            state: { record: record } as Record<string, unknown>,
                        });
                    }}
                >
                    {record.code}
                </Button>
            ),
        },
        //{ title: "Code", dataIndex: "code", key: "code" },
        { title: "Name", dataIndex: "policyHolderName", key: "policyHolderName" },
        { title: "Plan Name", dataIndex: "planName", key: "planName" },
        { title: "Category", dataIndex: "category", key: "category" },
        {
            title: "Inception Date", dataIndex: "inceptionDate", key: "inceptionDate",
            render: (text: string) => (text ? dayjs(text).format('DD-MM-YYYY') : ''),
        },
        {
            title: "Expiry Date", dataIndex: "expiryDate", key: "expiryDate",
            render: (text: string) => (text ? dayjs(text).format('DD-MM-YYYY') : ''),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <StatusTag status={status} />
            ),
        },
    ];

    return (
        <div>
            <DynamicTable<SearchPolicyType> // Ensure generic type matches the data
                dataApiUrl={API_ENDPOINTS.POLICIES.SEARCH_POLICY} // Your API endpoint for fetching menus
                baseQueryKey={QUERY_KEYS.POLICIES.Policy} // Unique query key for menus
                columns={SearchPolicyColumns}
                actionColumnWidth={200}
                tableTitle={"Policies"}
                showActions={{
                    edit: (record) => !record.isActive && record.status == "Created",
                    delete: (record) => !record.isActive && record.status == "Created",
                    view: false
                }}
                deleteBaseUrl={API_ENDPOINTS.POLICIES.DELETE_POLICY}
                customActions={myActions}
                rowSelectionEnabled={false} // Enable row selection
                onRowSelectionChange={handleRowSelectionChange} // Pass the handler
                selectedRowKeys={selectedPolicyRowKeys} // Pass the controlled selected keys
                // Pass the fields you want the search bar to filter
                filterConfigs={[
                    {
                        field: 'companyId',
                        type: 'select',
                        placeholder: "Select company",
                        showSearch: true,
                        dynamicOptions: {
                            url: "/Lookup/GetDropdownData",
                            method: "POST",
                            body: { "dropdowns": ["company"] },
                            transformData: (apiResponse: unknown) => {
                                const response = apiResponse as { company: { Name: string; Id: number }[] };
                                return response.company.map((item) => ({ label: item.Name, value: item.Id }))
                            }
                        },
                    },
                    { field: "name", placeholder:  t("Search by policy holder name")  },
                    { field: "code", placeholder:  t("Search by policy code")  },
                    {
                        field: "status", placeholder: t("Select Status"), type: 'select', showSearch: true,

                        options: POLICY_HOLDER_STATUS_OPTIONS

                    },
                    {
                        field: "Print",
                        type: "export",
                        exportOptions: {
                            url: API_ENDPOINTS.POLICIES.EXPORT_POLICY,
                            fileName: "Policy",
                            fileType: "excel", // "csv" 
                            defaultPayload: {},
                            columns: [
                                { header: "Code", key: "code" },
                                { header: "Name", key: "policyHolderName" },
                                { header: "Plan Name", key: "planName" },
                                { header: "Category", key: "category" },
                                {
                                    header: "Inception Date",
                                    key: "inceptionDate",
                                    transform: (date: string) => date ? dayjs(date).format('DD-MM-YYYY') : "-"
                                },
                                {
                                    header: "Expiry Date",
                                    key: "expiryDate",
                                    transform: (date: string) => date ? dayjs(date).format('DD-MM-YYYY') : "-"
                                },
                                { header: "Status", key: "status" }
                            ]
                        }
                    }
                ]}
            />
        </div>
    );

};

export default SearchPolicy;